import { useState, useEffect } from "react";
import { useSEO } from "@/hooks/useSEO";
import { Calendar, Clock, User, Mail, Phone, Users, FileText, CheckCircle, AlertCircle, MapPin, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pricing from the official form
const PRICING = {
  hall_hire_hourly: 20,
  hall_hire_full_day: 100,
  drapes: 50,
  chair_covers_per_set: 10,
  table_cloths_each: 5,
  av_equipment: 50,
  corkage_per_bottle: 4,
  hall_cleaning: 100
};

const PURPOSE_OPTIONS = [
  "Wedding Reception",
  "Birthday Party",
  "Anniversary Celebration",
  "Community Meeting",
  "Club Event",
  "Fitness Class",
  "Market/Fair",
  "Fundraiser",
  "Private Function",
  "Corporate Event",
  "Other"
];

export default function BookingPage() {
  const [selectedDate, setSelectedDate] = useState(null);
  const [bookedSlots, setBookedSlots] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [hallInfo, setHallInfo] = useState(null);

  useSEO({
    title: "Book The Hall | The Elizabethan Centre, Whaplode Drove",
    description: "Book The Elizabethan Centre online - check availability and submit your hall hire request. From £20/hour or £100/day. Perfect for weddings, parties and events in Lincolnshire.",
    canonical: "https://www.elizabethancentre.co.uk/book"
  });
  
  // Form data matching the official hire form
  const [formData, setFormData] = useState({
    // Hirer Details
    hirer_name: "",
    contact_person: "",
    address_line1: "",
    address_line2: "",
    address_line3: "",
    phone_number: "",
    email_address: "",
    
    // Event Details
    purpose_of_hire: "",
    number_of_attendees: "",
    start_time: "09:00",
    end_time: "13:00", // 9:00 + 4 hours
    
    // Hire Options
    hall_hire_type: "hourly", // "hourly" or "full_day"
    hall_hire_hours: 4,
    drapes: false,
    chair_covers_sets: 0,
    table_cloths: 0,
    av_equipment: false,
    corkage_bottles: 0,
    hall_cleaning: false,
    
    // Terms
    agreed_to_terms: false,
    special_requests: ""
  });

  const [errors, setErrors] = useState({});

  // Fetch availability and hall info
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [availRes, hallRes] = await Promise.all([
          axios.get(`${API}/availability`, {
            params: {
              month: currentMonth.getMonth() + 1,
              year: currentMonth.getFullYear()
            }
          }),
          axios.get(`${API}/hall-info`)
        ]);
        setBookedSlots(availRes.data.booked_slots || {});
        setHallInfo(hallRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load availability");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [currentMonth]);

  // Calculate total cost
  const calculateTotal = () => {
    let total = 0;
    
    if (formData.hall_hire_type === "full_day") {
      total += PRICING.hall_hire_full_day;
    } else {
      total += (formData.hall_hire_hours || 0) * PRICING.hall_hire_hourly;
    }
    
    if (formData.drapes) total += PRICING.drapes;
    total += (formData.chair_covers_sets || 0) * PRICING.chair_covers_per_set;
    total += (formData.table_cloths || 0) * PRICING.table_cloths_each;
    if (formData.av_equipment) total += PRICING.av_equipment;
    total += (formData.corkage_bottles || 0) * PRICING.corkage_per_bottle;
    if (formData.hall_cleaning) total += PRICING.hall_cleaning;
    
    return total;
  };

  // Check if date has full day booking or event
  // Format date to YYYY-MM-DD in local timezone (not UTC)
  const formatDateToString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDateStatus = (date) => {
    const dateStr = formatDateToString(date);
    const slots = bookedSlots[dateStr] || [];
    
    if (slots.length === 0) return "available";
    
    // Check if there's a full day booking
    if (slots.some(s => s.is_full_day)) return "booked";
    
    // Check if there's an event
    if (slots.some(s => s.type === "event")) return "event";
    
    return "partial";
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === "checkbox" ? checked : value 
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleNumberChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: Math.max(0, parseInt(value) || 0) }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!selectedDate) newErrors.date = "Please select a date";
    if (!formData.hirer_name.trim()) newErrors.hirer_name = "Hirer name is required";
    if (!formData.address_line1.trim()) newErrors.address_line1 = "Address is required";
    if (!formData.phone_number.trim()) newErrors.phone_number = "Phone number is required";
    if (!formData.email_address.trim()) {
      newErrors.email_address = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email_address)) {
      newErrors.email_address = "Invalid email address";
    }
    if (!formData.purpose_of_hire) newErrors.purpose_of_hire = "Please select purpose of hire";
    if (!formData.number_of_attendees || parseInt(formData.number_of_attendees) < 1) {
      newErrors.number_of_attendees = "Number of attendees is required";
    }
    if (!formData.agreed_to_terms) newErrors.agreed_to_terms = "You must agree to the terms and conditions";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    
    try {
      const bookingData = {
        booking_date: formatDateToString(selectedDate),
        start_time: formData.start_time,
        end_time: formData.end_time,
        purpose_of_hire: formData.purpose_of_hire,
        number_of_attendees: parseInt(formData.number_of_attendees),
        hirer_name: formData.hirer_name,
        contact_person: formData.contact_person || null,
        address_line1: formData.address_line1,
        address_line2: formData.address_line2 || null,
        address_line3: formData.address_line3 || null,
        phone_number: formData.phone_number,
        email_address: formData.email_address,
        hire_options: {
          hall_hire_hours: formData.hall_hire_type === "hourly" ? formData.hall_hire_hours : null,
          hall_hire_full_day: formData.hall_hire_type === "full_day",
          drapes: formData.drapes,
          chair_covers_sets: formData.chair_covers_sets,
          table_cloths: formData.table_cloths,
          av_equipment: formData.av_equipment,
          corkage_bottles: formData.corkage_bottles,
          hall_cleaning: formData.hall_cleaning
        },
        agreed_to_terms: formData.agreed_to_terms,
        special_requests: formData.special_requests || null
      };

      await axios.post(`${API}/bookings`, bookingData);
      
      setBookingSuccess(true);
      toast.success("Booking request submitted successfully!");
      
    } catch (error) {
      console.error("Error submitting booking:", error);
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || "This slot is already booked");
      } else {
        toast.error("Failed to submit booking. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Disable past dates
  const disabledDays = { before: new Date() };
  const totalCost = calculateTotal();
  const depositAmount = totalCost * 0.25;

  if (bookingSuccess) {
    return (
      <div data-testid="booking-success" className="min-h-[60vh] flex items-center justify-center py-20">
        <Card className="card-heritage max-w-lg mx-auto">
          <CardContent className="p-12 text-center">
            <div className="w-20 h-20 bg-[#DCFCE7] rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-[#15803D]" />
            </div>
            <h2 className="heading-md mb-4">Booking Request Submitted!</h2>
            <p className="text-body mb-4">
              Thank you for your booking request. We will review your request and 
              get back to you within 24-48 hours to confirm your booking.
            </p>
            <div className="bg-[#F5F5F0] rounded-lg p-4 mb-6 text-left">
              <p className="text-sm"><strong>Estimated Total:</strong> £{totalCost}</p>
              <p className="text-sm"><strong>Deposit Required (25%):</strong> £{depositAmount.toFixed(2)}</p>
            </div>
            <Button 
              className="btn-primary"
              onClick={() => {
                setBookingSuccess(false);
                setFormData({
                  hirer_name: "", contact_person: "", address_line1: "", address_line2: "", address_line3: "",
                  phone_number: "", email_address: "", purpose_of_hire: "", number_of_attendees: "",
                  start_time: "09:00", end_time: "13:00", hall_hire_type: "hourly", hall_hire_hours: 4,
                  drapes: false, chair_covers_sets: 0, table_cloths: 0, av_equipment: false, corkage_bottles: 0,
                  hall_cleaning: false, agreed_to_terms: false, special_requests: ""
                });
                setSelectedDate(null);
              }}
            >
              Make Another Booking
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="booking-page">
      {/* Hero Section */}
      <section className="relative py-20 bg-[#F5F5F0]" data-testid="booking-hero">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="inline-block bg-[#14532D]/10 text-[#14532D] text-sm font-medium px-4 py-1.5 rounded-full mb-6">
              Hall Hire Form
            </span>
            <h1 className="heading-xl mb-4">Book the Hall</h1>
            <p className="text-xl text-[#292524]">
              Complete this form to request a booking for Whaplode Drove Social Club Hall.
            </p>
          </div>
        </div>
      </section>

      {/* Booking Form Section */}
      <section className="section-padding" data-testid="booking-form-section">
        <div className="container-custom">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              
              {/* Left Column - Date Selection */}
              <div className="lg:col-span-1">
                <Card className="card-heritage sticky top-24">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3 font-serif">
                      <Calendar className="w-6 h-6 text-[#14532D]" />
                      Select Date
                    </CardTitle>
                    <CardDescription>Choose your preferred date</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <CalendarComponent
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={disabledDays}
                      onMonthChange={setCurrentMonth}
                      modifiers={{
                        booked: (date) => getDateStatus(date) === "booked",
                        partial: (date) => getDateStatus(date) === "partial",
                        event: (date) => getDateStatus(date) === "event"
                      }}
                      modifiersStyles={{
                        booked: { backgroundColor: '#FEE2E2', color: '#B91C1C' },
                        partial: { backgroundColor: '#FEF3C7', color: '#C2410C' },
                        event: { backgroundColor: '#DBEAFE', color: '#1D4ED8' }
                      }}
                      className="rounded-md border w-full"
                      data-testid="booking-calendar"
                    />
                    
                    {/* Legend */}
                    <div className="flex flex-wrap gap-3 text-xs">
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 rounded bg-white border border-[#E7E5E4]" />
                        Available
                      </span>
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 rounded bg-[#DBEAFE]" />
                        Event
                      </span>
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 rounded bg-[#FEF3C7]" />
                        Partial
                      </span>
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 rounded bg-[#FEE2E2]" />
                        Booked
                      </span>
                    </div>

                    {errors.date && !selectedDate && (
                      <p className="error-message">{errors.date}</p>
                    )}

                    {/* Show event info if date has event */}
                    {selectedDate && getDateStatus(selectedDate) === "event" && (
                      <div className="p-3 bg-[#DBEAFE] border border-[#93C5FD] rounded-lg animate-slide-up">
                        <p className="text-sm text-[#1D4ED8] font-medium">
                          ℹ️ There is an event scheduled on this date:
                        </p>
                        {bookedSlots[formatDateToString(selectedDate)]?.filter(s => s.type === "event").map((event) => (
                          <p key={`${event.title}-${event.start_time}`} className="text-sm text-[#1D4ED8] mt-1">
                            <strong>{event.title}</strong> ({event.start_time} - {event.end_time})
                          </p>
                        ))}
                        <p className="text-xs text-[#1D4ED8]/70 mt-2">
                          You may still book outside event hours if available.
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Middle & Right - Form Details */}
              <div className="lg:col-span-2 space-y-8">
                
                {/* Hirer Details */}
                <Card className="card-heritage">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3 font-serif">
                      <User className="w-6 h-6 text-[#14532D]" />
                      Hirer Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="hirer_name" className="label-heritage">Hirer Name *</Label>
                        <Input
                          id="hirer_name"
                          name="hirer_name"
                          value={formData.hirer_name}
                          onChange={handleInputChange}
                          className={`input-heritage ${errors.hirer_name ? 'input-error' : ''}`}
                          placeholder="Full name of hirer"
                          data-testid="input-hirer-name"
                        />
                        {errors.hirer_name && <p className="error-message">{errors.hirer_name}</p>}
                      </div>
                      <div>
                        <Label htmlFor="contact_person" className="label-heritage">Contact Person (if different)</Label>
                        <Input
                          id="contact_person"
                          name="contact_person"
                          value={formData.contact_person}
                          onChange={handleInputChange}
                          className="input-heritage"
                          placeholder="Contact person name"
                          data-testid="input-contact-person"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="address_line1" className="label-heritage flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        Address *
                      </Label>
                      <Input
                        id="address_line1"
                        name="address_line1"
                        value={formData.address_line1}
                        onChange={handleInputChange}
                        className={`input-heritage mb-2 ${errors.address_line1 ? 'input-error' : ''}`}
                        placeholder="Address line 1"
                        data-testid="input-address1"
                      />
                      <Input
                        name="address_line2"
                        value={formData.address_line2}
                        onChange={handleInputChange}
                        className="input-heritage mb-2"
                        placeholder="Address line 2"
                        data-testid="input-address2"
                      />
                      <Input
                        name="address_line3"
                        value={formData.address_line3}
                        onChange={handleInputChange}
                        className="input-heritage"
                        placeholder="Town / Postcode"
                        data-testid="input-address3"
                      />
                      {errors.address_line1 && <p className="error-message">{errors.address_line1}</p>}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="phone_number" className="label-heritage flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          Phone Number *
                        </Label>
                        <Input
                          id="phone_number"
                          name="phone_number"
                          type="tel"
                          value={formData.phone_number}
                          onChange={handleInputChange}
                          className={`input-heritage ${errors.phone_number ? 'input-error' : ''}`}
                          placeholder="01234 567890"
                          data-testid="input-phone"
                        />
                        {errors.phone_number && <p className="error-message">{errors.phone_number}</p>}
                      </div>
                      <div>
                        <Label htmlFor="email_address" className="label-heritage flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          Email Address *
                        </Label>
                        <Input
                          id="email_address"
                          name="email_address"
                          type="email"
                          value={formData.email_address}
                          onChange={handleInputChange}
                          className={`input-heritage ${errors.email_address ? 'input-error' : ''}`}
                          placeholder="your@email.com"
                          data-testid="input-email"
                        />
                        {errors.email_address && <p className="error-message">{errors.email_address}</p>}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Event Details */}
                <Card className="card-heritage">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3 font-serif">
                      <FileText className="w-6 h-6 text-[#14532D]" />
                      Event Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="purpose_of_hire" className="label-heritage">Purpose of Hire *</Label>
                        <select
                          id="purpose_of_hire"
                          name="purpose_of_hire"
                          value={formData.purpose_of_hire}
                          onChange={handleInputChange}
                          className={`input-heritage w-full ${errors.purpose_of_hire ? 'input-error' : ''}`}
                          data-testid="select-purpose"
                        >
                          <option value="">Select purpose...</option>
                          {PURPOSE_OPTIONS.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                        {errors.purpose_of_hire && <p className="error-message">{errors.purpose_of_hire}</p>}
                      </div>
                      <div>
                        <Label htmlFor="number_of_attendees" className="label-heritage flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          Number of Attendees *
                        </Label>
                        <Input
                          id="number_of_attendees"
                          name="number_of_attendees"
                          type="number"
                          value={formData.number_of_attendees}
                          onChange={handleInputChange}
                          className={`input-heritage ${errors.number_of_attendees ? 'input-error' : ''}`}
                          placeholder="Expected guests"
                          min="1"
                          max="200"
                          data-testid="input-attendees"
                        />
                        {errors.number_of_attendees && <p className="error-message">{errors.number_of_attendees}</p>}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Hire Options & Charges */}
                <Card className="card-heritage">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3 font-serif">
                      <Sparkles className="w-6 h-6 text-[#14532D]" />
                      Hire Charges
                    </CardTitle>
                    <CardDescription>Select the items you wish to hire</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Hall Hire Type */}
                    <div className="p-4 bg-[#F5F5F0] rounded-lg">
                      <Label className="label-heritage mb-3 block">Hall Hire *</Label>
                      <div className="flex flex-col sm:flex-row gap-4">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="radio"
                            name="hall_hire_type"
                            value="hourly"
                            checked={formData.hall_hire_type === "hourly"}
                            onChange={handleInputChange}
                            className="w-4 h-4 text-[#14532D]"
                          />
                          <span>Hourly (£{PRICING.hall_hire_hourly}/hr, max 4 hours)</span>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="radio"
                            name="hall_hire_type"
                            value="full_day"
                            checked={formData.hall_hire_type === "full_day"}
                            onChange={handleInputChange}
                            className="w-4 h-4 text-[#14532D]"
                          />
                          <span>Full Day (£{PRICING.hall_hire_full_day})</span>
                        </label>
                      </div>
                      
                      {formData.hall_hire_type === "hourly" && (
                        <div className="mt-4 space-y-4 p-4 bg-white rounded-lg border border-[#E7E5E4]">
                          <div className="flex items-center gap-3">
                            <Label className="text-sm">Number of hours:</Label>
                            <Input
                              type="number"
                              value={formData.hall_hire_hours}
                              onChange={(e) => {
                                const val = Math.min(4, Math.max(1, parseInt(e.target.value) || 1));
                                setFormData(prev => {
                                  // Calculate new end time based on start time + hours
                                  const [startHour, startMin] = prev.start_time.split(':').map(Number);
                                  const endHour = startHour + val;
                                  const newEndTime = `${String(Math.min(23, endHour)).padStart(2, '0')}:${String(startMin).padStart(2, '0')}`;
                                  return { ...prev, hall_hire_hours: val, end_time: newEndTime };
                                });
                              }}
                              className="input-heritage w-24"
                              min="1"
                              max="4"
                              data-testid="input-hours"
                            />
                            <span className="text-xs text-[#78716C]">(1-4 hours, 5+ hours = full day)</span>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="label-heritage flex items-center gap-2">
                                <Clock className="w-4 h-4" />
                                Start Time
                              </Label>
                              <Input
                                type="time"
                                name="start_time"
                                value={formData.start_time}
                                onChange={(e) => {
                                  const newStartTime = e.target.value;
                                  const [startHour, startMin] = newStartTime.split(':').map(Number);
                                  const endHour = startHour + formData.hall_hire_hours;
                                  const newEndTime = `${String(Math.min(23, endHour)).padStart(2, '0')}:${String(startMin).padStart(2, '0')}`;
                                  setFormData(prev => ({ ...prev, start_time: newStartTime, end_time: newEndTime }));
                                }}
                                className="input-heritage"
                                data-testid="input-start-time"
                              />
                            </div>
                            <div>
                              <Label className="label-heritage flex items-center gap-2">
                                <Clock className="w-4 h-4" />
                                End Time
                              </Label>
                              <Input
                                type="time"
                                name="end_time"
                                value={formData.end_time}
                                readOnly
                                className="input-heritage bg-[#F5F5F0] cursor-not-allowed"
                                data-testid="input-end-time"
                              />
                              <p className="text-xs text-[#78716C] mt-1">Auto-calculated based on hours</p>
                            </div>
                          </div>
                        </div>
                      )}
                      <p className="text-xs text-[#78716C] mt-2">Includes tables, chairs, and use of kitchen facilities</p>
                    </div>

                    {/* Additional Items */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Drapes */}
                      <div className="p-4 border border-[#E7E5E4] rounded-lg">
                        <label className="flex items-center justify-between cursor-pointer">
                          <div className="flex items-center gap-3">
                            <Checkbox
                              checked={formData.drapes}
                              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, drapes: checked }))}
                              data-testid="checkbox-drapes"
                            />
                            <span className="font-medium">Drapes</span>
                          </div>
                          <span className="font-mono text-[#14532D] font-bold">£{PRICING.drapes}</span>
                        </label>
                      </div>

                      {/* AV Equipment */}
                      <div className="p-4 border border-[#E7E5E4] rounded-lg">
                        <label className="flex items-center justify-between cursor-pointer">
                          <div className="flex items-center gap-3">
                            <Checkbox
                              checked={formData.av_equipment}
                              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, av_equipment: checked }))}
                              data-testid="checkbox-av"
                            />
                            <span className="font-medium">AV/Lighting Equipment</span>
                          </div>
                          <span className="font-mono text-[#14532D] font-bold">£{PRICING.av_equipment}</span>
                        </label>
                      </div>

                      {/* Hall Cleaning */}
                      <div className="p-4 border border-[#E7E5E4] rounded-lg">
                        <label className="flex items-center justify-between cursor-pointer">
                          <div className="flex items-center gap-3">
                            <Checkbox
                              checked={formData.hall_cleaning}
                              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, hall_cleaning: checked }))}
                              data-testid="checkbox-cleaning"
                            />
                            <div>
                              <span className="font-medium">Hall Clearing & Cleaning</span>
                              <p className="text-xs text-[#78716C]">We clear and clean the hall after your event</p>
                            </div>
                          </div>
                          <span className="font-mono text-[#14532D] font-bold">£{PRICING.hall_cleaning}</span>
                        </label>
                      </div>

                      {/* Chair Covers */}
                      <div className="p-4 border border-[#E7E5E4] rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">Chair Covers</span>
                          <span className="text-xs text-[#78716C]">£{PRICING.chair_covers_per_set} per 20</span>
                        </div>
                        <div className="flex items-center gap-3 mt-2">
                          <Label className="text-sm">Sets of 20:</Label>
                          <Input
                            type="number"
                            value={formData.chair_covers_sets}
                            onChange={(e) => handleNumberChange("chair_covers_sets", e.target.value)}
                            className="input-heritage w-20"
                            min="0"
                            data-testid="input-chair-covers"
                          />
                        </div>
                      </div>

                      {/* Table Cloths */}
                      <div className="p-4 border border-[#E7E5E4] rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">Table Cloths</span>
                          <span className="text-xs text-[#78716C]">£{PRICING.table_cloths_each} each</span>
                        </div>
                        <div className="flex items-center gap-3 mt-2">
                          <Label className="text-sm">Quantity:</Label>
                          <Input
                            type="number"
                            value={formData.table_cloths}
                            onChange={(e) => handleNumberChange("table_cloths", e.target.value)}
                            className="input-heritage w-20"
                            min="0"
                            data-testid="input-table-cloths"
                          />
                        </div>
                      </div>

                      {/* Our Partners */}
                      <div className="p-4 border border-[#14532D]/20 rounded-lg bg-[#F0FDF4] sm:col-span-2">
                        <span className="font-medium text-[#14532D]">Our Partners</span>
                        <div className="mt-2 space-y-1 text-sm">
                          <p>
                            <span className="text-[#78716C]">Disco Hire: </span>
                            <a 
                              href="https://www.eminanceentertainments.co.uk" 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-[#14532D] hover:underline font-medium"
                            >
                              www.eminanceentertainments.co.uk
                            </a>
                          </p>
                          <p>
                            <span className="text-[#78716C]">Table, Plate & Glass Hire: </span>
                            <a 
                              href="https://www.peacheventshire.co.uk" 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-[#14532D] hover:underline font-medium"
                            >
                              www.peacheventshire.co.uk
                            </a>
                          </p>
                        </div>
                      </div>

                      {/* Corkage */}
                      <div className="p-4 border border-[#E7E5E4] rounded-lg sm:col-span-2">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">Corkage</span>
                          <span className="text-xs text-[#78716C]">£{PRICING.corkage_per_bottle} per bottle</span>
                        </div>
                        <p className="text-xs text-[#C2410C] mt-1">Only applies to wine (still & sparkling). All other alcohol must be purchased on premises.</p>
                        <div className="flex items-center gap-3 mt-2">
                          <Label className="text-sm">Number of bottles:</Label>
                          <Input
                            type="number"
                            value={formData.corkage_bottles}
                            onChange={(e) => handleNumberChange("corkage_bottles", e.target.value)}
                            className="input-heritage w-20"
                            min="0"
                            data-testid="input-corkage"
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Special Requests */}
                <Card className="card-heritage">
                  <CardHeader>
                    <CardTitle className="font-serif">Special Requests</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Textarea
                      name="special_requests"
                      value={formData.special_requests}
                      onChange={handleInputChange}
                      className="input-heritage min-h-[100px] resize-none"
                      placeholder="Any special requirements or questions..."
                      data-testid="input-requests"
                    />
                  </CardContent>
                </Card>

                {/* Terms and Total */}
                <Card className="card-heritage border-2 border-[#14532D]">
                  <CardHeader>
                    <CardTitle className="font-serif">Terms & Conditions</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Terms List */}
                    <div className="bg-[#F5F5F0] rounded-lg p-4 max-h-48 overflow-y-auto text-sm space-y-2">
                      {hallInfo?.terms?.map((term) => (
                        <p key={term} className="text-[#292524]">• {term}</p>
                      ))}
                    </div>

                    {/* Agreement Checkbox */}
                    <div className={`flex items-start gap-3 p-4 rounded-lg ${errors.agreed_to_terms ? 'bg-[#FEE2E2]' : 'bg-white'}`}>
                      <Checkbox
                        id="agreed_to_terms"
                        checked={formData.agreed_to_terms}
                        onCheckedChange={(checked) => {
                          setFormData(prev => ({ ...prev, agreed_to_terms: checked }));
                          if (errors.agreed_to_terms) {
                            setErrors(prev => ({ ...prev, agreed_to_terms: null }));
                          }
                        }}
                        data-testid="checkbox-terms"
                      />
                      <label htmlFor="agreed_to_terms" className="text-sm cursor-pointer">
                        I have read and agree to the Terms and Conditions *
                      </label>
                    </div>
                    {errors.agreed_to_terms && <p className="error-message">{errors.agreed_to_terms}</p>}

                    {/* Cost Summary */}
                    <div className="bg-[#14532D] text-white rounded-lg p-6">
                      <h4 className="font-semibold mb-4 text-lg">Booking Summary</h4>
                      {selectedDate && (
                        <p className="text-white/80 mb-2">
                          Date: {selectedDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                      )}
                      <div className="border-t border-white/20 pt-4 mt-4 space-y-2">
                        <div className="flex justify-between text-lg">
                          <span>Total Cost:</span>
                          <span className="font-mono font-bold">£{totalCost}</span>
                        </div>
                        <div className="flex justify-between text-white/80">
                          <span>Deposit Required (25%):</span>
                          <span className="font-mono">£{depositAmount.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <Button 
                      type="submit" 
                      className="btn-primary w-full h-14 text-lg"
                      disabled={submitting || !selectedDate}
                      data-testid="submit-booking-btn"
                    >
                      {submitting ? (
                        <>
                          <span className="spinner mr-2" />
                          Submitting...
                        </>
                      ) : (
                        "Submit Booking Request"
                      )}
                    </Button>

                    <p className="text-xs text-[#78716C] text-center">
                      Your booking will be reviewed and confirmed within 24-48 hours. 
                      You will receive an email with payment instructions.
                    </p>
                  </CardContent>
                </Card>

              </div>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
